import { ProductType } from '@reg/shared/model/product-type.model';
import { Event } from '@reg/shared/model/event.model';
import get from 'lodash-es/get';
import { IJsonApiEntity, IJsonApiRelationships } from '@reg/shared/model/json-api';
import { UserAccount } from '@reg/shared/model/user-account.model';
import { zonedTimeToUtc } from 'date-fns-tz';

/**
 * Model to hold OrderCoupon details
 */
export class OrderCoupon {
    public id: string;
    public code: string;
    public usesAllowed: number;
    public usedCount: number;
    public dollarDiscount: number;
    public percentageDiscount: number;
    public expirationDateTime: Date;
    public productTypes: ProductType[] = null;
    public events: Event[] = null;
    public createdByUserAccount: UserAccount;

    public get remainingCount(): number {
        const remaining: number = (this.usesAllowed || 0) - (this.usedCount || 0);
        return remaining < 0 ? 0 : remaining;
    }

    public get isUsed(): boolean {
        return (this.usedCount || 0) > 0;
    }

    public clone(): OrderCoupon {
        const clone: OrderCoupon = new OrderCoupon();
        clone.id = this.id;
        clone.code = this.code;
        clone.usesAllowed = this.usesAllowed;
        clone.usedCount = this.usedCount;
        clone.dollarDiscount = this.dollarDiscount;
        clone.percentageDiscount = this.percentageDiscount;
        clone.expirationDateTime = this.expirationDateTime ? new Date(this.expirationDateTime.getTime()) : null;
        clone.productTypes = this.productTypes && this.productTypes.map((t: ProductType) => t.clone());
        clone.events = this.events && this.events.map((e: Event) => e.clone());
        clone.createdByUserAccount = this.createdByUserAccount && this.createdByUserAccount.clone();

        return clone;
    }

    /**
     * Serialize from to json api format
     */
    public serialize(): IJsonApiOrderCoupon {
        const json: IJsonApiOrderCoupon = {
            data: {
                id: this.id,
                type: 'orderCoupon',
                attributes: {
                    code: this.code,
                    usesAllowed: parseFloat((this.usesAllowed || 0).toString()),
                    dollarDiscount: parseFloat((this.dollarDiscount || 0).toString()),
                    percentageDiscount: parseFloat((this.percentageDiscount || 0).toString()),
                    expirationDateTimeUtc: this.expirationDateTime ? this.expirationDateTime.toISOString() : null,
                },
                relationships: {},
            },
        };

        if (this.productTypes !== null) {
            json.data.relationships.productTypes = {
                data: this.productTypes.map((t: ProductType) => ({ type: 'productType', id: t.id })),
            };
        }
        if (this.events !== null) {
            json.data.relationships.events = {
                data: this.events.map((e: Event) => ({ type: 'event', id: e.id })),
            };
        }

        return json;
    }

    /**
     * Deserialize from a json api format
     */
    public deserialize(json: any, included: any[]): OrderCoupon {
        const data: any = json.data || json;
        const attrs: any = data.attributes;
        included = included || [];

        this.id = data.id;
        this.code = attrs.code;
        this.usesAllowed = attrs.usesAllowed;
        this.usedCount = attrs.usedCount;
        this.dollarDiscount = attrs.dollarDiscount;
        this.percentageDiscount = attrs.percentageDiscount;

        if (attrs.expirationDateTimeUtc) {
            const localTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
            this.expirationDateTime = zonedTimeToUtc(attrs.expirationDateTimeUtc, localTimezone);
        }

        // Fill product types
        const productTypes: any[] = get(data, 'relationships.productTypes.data') || [];
        this.productTypes = productTypes.length ? [] : this.productTypes;
        productTypes.forEach((x: any) => {
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                this.productTypes.push((new ProductType()).deserialize(item));
            }
        });

        // Fill events
        const events: any[] = get(data, 'relationships.events.data') || [];
        this.events = events.length ? [] : this.events;
        events.forEach((x: any) => {
            this.events = this.events || [];
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                this.events.push((new Event()).deserialize(item));
            }
        });

        // Fill createdByUserAccount from relationships if it exists
        const createdByUserId: string = get(data, 'relationships.createdByUserAccount.data.id');
        const createdByUser: any = included.find((i: any) => i.type === 'userAccount' && i.id === createdByUserId);

        if (createdByUser) {
            this.createdByUserAccount = (new UserAccount()).deserialize(createdByUser);
        } else if (attrs.createdByUserId) {
            this.createdByUserAccount = new UserAccount(attrs.createdByUserId);
        }

        return this;
    }
}

export type IJsonApiOrderCoupon = IJsonApiEntity<IOrderCouponAttributes, IOrderCouponRelationships>;

export interface IOrderCouponAttributes {
    code?: string;
    codePrefix?: string;
    generateCount?: number;
    dollarDiscount: number;
    percentageDiscount: number;
    usesAllowed: number;
    expirationDateTimeUtc: string;
}

export interface IOrderCouponRelationships {
    events?: IJsonApiRelationships;
    productTypes?: IJsonApiRelationships;
}
